import { FormEvent, useState } from 'react';
import type { UserRole } from '@rosita/shared';
import { api, extractError } from '../../lib/api';
import { PhoneInput } from '../ui/PhoneInput';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from './Toast';

const inputClass =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-1.5 text-sm hover:bg-admin-surface-alt';

const ROLE_LABELS: Record<string, string> = {
  store_manager: 'Gestor de loja',
  admin: 'Administrador',
  super_admin: 'Super admin',
};

interface CreateForm {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: UserRole;
}

interface CreateUserModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export function CreateUserModal({ onClose, onSaved }: CreateUserModalProps): JSX.Element {
  const toast = useToast();
  const currentRole = useAuthStore((s) => s.user?.role);
  // super_admin can grant any staff role; everyone else stops at admin.
  const assignableRoles: UserRole[] =
    currentRole === 'super_admin'
      ? ['store_manager', 'admin', 'super_admin']
      : ['store_manager', 'admin'];

  const [form, setForm] = useState<CreateForm>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'store_manager',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/admin/users', {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        role: form.role,
      });
      toast.success('Utilizador criado.');
      onSaved();
    } catch (err) {
      toast.error(extractError(err, 'Falha ao criar utilizador'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-lg font-semibold">Novo utilizador</h2>
        <form onSubmit={submit} className="grid gap-3">
          <div>
            <label className="block text-sm text-admin-text-soft">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className={`${inputClass} mt-1 w-full`}
            />
          </div>
          <div>
            <label className="block text-sm text-admin-text-soft">Palavra-passe</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
              autoComplete="new-password"
              className={`${inputClass} mt-1 w-full`}
            />
            <p className="mt-1 text-xs text-admin-muted">Mínimo 8 caracteres.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm text-admin-text-soft">Primeiro nome</label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                required
                className={`${inputClass} mt-1 w-full`}
              />
            </div>
            <div>
              <label className="block text-sm text-admin-text-soft">Apelido</label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                required
                className={`${inputClass} mt-1 w-full`}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-admin-text-soft">Telefone</label>
            <PhoneInput
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
          </div>
          <div>
            <label className="block text-sm text-admin-text-soft">Papel</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as UserRole })}
              className={`${inputClass} mt-1 w-full`}
            >
              {assignableRoles.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r] ?? r}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className={secondaryBtn}
            >
              Cancelar
            </button>
            <button type="submit" disabled={saving} className={primaryBtn}>
              {saving ? 'A criar…' : 'Criar utilizador'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
